import React, { useState } from 'react';
import { useDraggableY } from '@/hooks/useDraggableY';
import CustomLink from '@common/CustomLink';
import { PageAccessibilityButton } from '@common/Providers/GraphqlProvider/generated';
import { ACCESSIBILITY_ICON_LIST } from '@views/Admin/private/AccessibilityButton/Add';
import { IoClose, IoMenu } from 'react-icons/io5';

type Props = {
  accessibilityButton?: PageAccessibilityButton;
};

const AccessibilityButton = ({ accessibilityButton }: Props) => {
  const { position, handleMouseDown, isDrag } = useDraggableY();
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  if (!accessibilityButton || !accessibilityButton.items) {
    return null;
  }

  const sortedItems = [...accessibilityButton.items]
    .filter((item) => item.isActive)
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const handleMainButtonClick = () => {
    if (!isDrag) {
      setIsOpen(!isOpen);
    }
  };

  const getIcon = (iconType: string) => {
    const iconConfig = ACCESSIBILITY_ICON_LIST.find((icon) => icon.value === iconType);
    return iconConfig ? iconConfig.label : null;
  };

  const getArcPosition = (index: number, total: number) => {
    const radius = 110;

    const baseAngleRange = Math.PI / 2;
    const maxAngleRange = Math.PI * 0.85;

    const angleRange = Math.min(baseAngleRange + (total - 1) * 0.15, maxAngleRange);

    const startAngle = -angleRange / 2;

    const angle = total > 1 ? startAngle + (angleRange * index) / (total - 1) : 0;

    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  };

  return (
    <div
      className="fixed left-0 top-[190px] z-[100]"
      style={{ top: position?.top }}
      onMouseDown={handleMouseDown}
    >
      <div className="relative">
        <div
          className="relative z-10"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={handleMainButtonClick}
            aria-label={isOpen ? 'Cerrar menú de acceso rápido' : 'Abrir menú de acceso rápido'}
            className="group relative flex select-none items-center overflow-hidden rounded-r-xl bg-primary shadow-lg transition-all duration-300 hover:shadow-xl active:scale-95"
            style={{
              width: isHovered && !isOpen ? '180px' : '56px',
              height: '56px',
            }}
          >
            <div className="absolute left-4 transition-all duration-300">
              <div className="relative flex h-6 w-6 items-center justify-center">
                <IoMenu
                  className={`absolute text-white transition-all duration-300 ${
                    isOpen ? 'rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100'
                  }`}
                  size={24}
                />

                <IoClose
                  className={`absolute text-white transition-all duration-300 ${
                    isOpen ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-50 opacity-0'
                  }`}
                  size={24}
                />
              </div>
            </div>

            <div
              className={`absolute right-4 whitespace-nowrap font-medium text-white transition-all duration-300 ${
                isHovered && !isOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
              }`}
            >
              Acceso Rápido
            </div>
          </button>

          {!isOpen && (
            <div
              className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-primary opacity-20"
              style={{ animationDuration: '3s' }}
            />
          )}
        </div>

        {sortedItems.map((item, index) => {
          const position = getArcPosition(index, sortedItems.length);
          const isVisible = isOpen;

          return (
            <div
              key={item.id}
              className={`absolute left-7 top-7 -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ease-out ${
                isVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
              }`}
              style={{
                transform: isVisible
                  ? `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(1)`
                  : 'translate(-50%, -50%) scale(0.3)',
                transitionDelay: isVisible ? `${index * 0.08}s` : '0s',
              }}
            >
              <CustomLink pageLink={item.linkItem}>
                <div
                  aria-label={item.altText}
                  title={item.linkName || item.altText}
                  className="group relative"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary bg-white shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl active:scale-95">
                    <div className="text-primary transition-transform duration-300 group-hover:rotate-12">
                      {getIcon(item.iconType)}
                    </div>
                  </div>

                  <div className="pointer-events-none absolute left-full top-1/2 ml-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-3 py-1.5 text-sm text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    {item.linkName || item.altText}
                    <div className="absolute left-0 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-gray-800"></div>
                  </div>
                </div>
              </CustomLink>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccessibilityButton;
